// @ts-nocheck
import React, { useState } from 'react';
import { PlusIcon, SparklesIcon, CloseIcon, CameraIcon, PaperclipIcon, MicIcon, SendIcon, CopyIcon, PasteIcon, SearchIcon, MailIcon, ChevronLeftIcon, ChevronRightIcon, SettingsIcon, EditIcon, VolumeHighIcon, MoreHorizontalIcon, TrashIcon, EmojiIcon, ExternalLinkIcon } from './Icons';

export type PlaygroundElementType = 'button' | 'pfp' | 'tab' | 'setting_bar' | 'modal';

export interface PlaygroundElement {
    id: string;
    type: PlaygroundElementType;
    variant: string;
    x: number;
    y: number;
    props: any;
}

interface VisualModPlaygroundProps {
    elements: PlaygroundElement[];
    onAddElement: (type: PlaygroundElementType, variant: string, props: any) => void;
    onUpdateElement: (id: string, updates: Partial<PlaygroundElement>) => void;
    onRemoveElement: (id: string) => void;
    isPhone: boolean;
}

const MODULAR_COMPONENTS = [
    {
        category: 'Buttons',
        items: [
            { type: 'button', variant: 'pill', label: 'Follow', props: { label: 'Follow', variant: 'blue' } },
            { type: 'button', variant: 'pill', label: 'Message', props: { label: 'Message', variant: 'glass' } },
            { type: 'button', variant: 'icon', label: 'Plus Icon', props: { icon: 'PlusIcon' } },
            { type: 'button', variant: 'text', label: 'Fullscreen', props: { label: 'Fullscreen' } },
            { type: 'button', variant: 'text', label: 'Snap', props: { label: 'Snap' } },
            { type: 'button', variant: 'pill', label: 'Open Modal', props: { label: 'Open Modal', variant: 'blue', action: 'open_modal' } },
        ]
    },
    {
        category: 'Actions (Assignable)',
        items: [
            { type: 'button', variant: 'pill', label: 'Copy Action', props: { label: 'Copy Content', variant: 'glass', action: 'copy' } },
            { type: 'button', variant: 'pill', label: 'Upload Action', props: { label: 'Upload File', variant: 'glass', action: 'upload' } },
            { type: 'button', variant: 'pill', label: 'Record Action', props: { label: 'Record Audio', variant: 'glass', action: 'record' } },
        ]
    },
    {
        category: 'PFPs',
        items: [
            { type: 'pfp', variant: 'sm', label: 'PFP Small', props: { size: 'sm', navigateToProfile: true } },
            { type: 'pfp', variant: 'md', label: 'PFP Medium', props: { size: 'md', navigateToProfile: true } },
            { type: 'pfp', variant: 'lg', label: 'PFP Large', props: { size: 'lg', navigateToProfile: true } },
            { type: 'pfp', variant: 'xl', label: 'PFP Extra Large', props: { size: 'xl', navigateToProfile: true } },
        ]
    },
    {
        category: 'Tabs',
        items: [
            { type: 'tab', variant: 'standard', label: 'Standard Tabs', props: { items: ['Feed', 'Likes', 'Tags'] } },
            { type: 'tab', variant: 'bubble', label: 'Bubble Tabs', props: { items: ['New', 'Top', 'Hot'] } },
            { type: 'tab', variant: 'options', label: 'Options Tabs', props: { items: ['All', 'Groups'] } },
        ]
    },
    {
        category: 'Settings',
        items: [
            { type: 'setting_bar', variant: 'color', label: 'Color Bar', props: { label: 'Text Color', color: '#3b82f6' } },
        ]
    }
];

export const VisualModPlayground: React.FC<VisualModPlaygroundProps> = ({ elements, onAddElement, onUpdateElement, onRemoveElement, isPhone }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [dragState, setDragState] = useState<{ id: string; startX: number; startY: number; initialX: number; initialY: number } | null>(null);

    const [activeModalId, setActiveModalId] = useState<string | null>(null);
    const [configuringItem, setConfiguringItem] = useState<{ type: string; variant: string; props: any } | null>(null);
    const [configLabel, setConfigLabel] = useState('');
    const [configAction, setConfigAction] = useState('none');

    const handleAddWithConfig = () => {
        if (!configuringItem) return;
        const finalProps = { ...configuringItem.props };
        
        const currentProps = configuringItem.props as any;
        if (configuringItem.type === 'button') {
            finalProps.label = configLabel || currentProps.label;
            finalProps.action = configAction !== 'none' ? configAction : currentProps.action;
        } else if (configuringItem.type === 'pfp') {
            finalProps.size = configLabel || currentProps.size; 
            finalProps.navigateToProfile = configAction === 'true';
        } else if (configuringItem.type === 'tab') {
            finalProps.items = configLabel.split(',').map(s => s.trim()) || currentProps.items;
        } else if (configuringItem.type === 'setting_bar') {
            finalProps.label = configLabel || currentProps.label;
        }

        onAddElement(configuringItem.type as any, configuringItem.variant, finalProps);
        setConfiguringItem(null);
        setConfigLabel('');
        setConfigAction('none');
        setShowMenu(false);
    };

    const handleDragStart = (e: React.MouseEvent | React.TouchEvent, id: string) => {
        const element = elements.find(el => el.id === id);
        if (!element) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        setDragState({
            id,
            startX: clientX,
            startY: clientY,
            initialX: element.x,
            initialY: element.y
        });
    };

    const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!dragState) return;

        const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

        const dx = clientX - dragState.startX;
        const dy = clientY - dragState.startY;

        onUpdateElement(dragState.id, {
            x: dragState.initialX + dx,
            y: dragState.initialY + dy
        });
    };

    const handleDragEnd = () => {
        setDragState(null);
    };

    const renderElement = (el: PlaygroundElement) => {
        const style: React.CSSProperties = {
            position: 'absolute',
            left: el.x,
            top: el.y,
            cursor: 'move',
            zIndex: 100,
            userSelect: 'none',
            touchAction: 'none'
        };

        let content = null;

        if (el.type === 'button') {
            const handleClick = () => {
                if (el.props.action === 'open_modal') {
                    setActiveModalId(el.id);
                } else if (el.props.action) {
                    console.log(`[Preview Action] Triggered: ${el.props.action}`);
                }
            };

            if (el.variant === 'pill') {
                content = (
                    <button 
                        onClick={handleClick}
                        className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${el.props.variant === 'blue' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white/10 text-white/80 border border-white/10'}`}
                    >
                        {el.props.label}
                    </button>
                );
            } else if (el.variant === 'icon') {
                content = (
                    <button 
                        onClick={handleClick}
                        className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:text-white"
                    >
                        <PlusIcon className="w-5 h-5" />
                    </button>
                );
            } else if (el.variant === 'text') {
                content = (
                    <button 
                        onClick={handleClick}
                        className="text-[10px] font-black text-white/40 hover:text-white uppercase tracking-widest"
                    >
                        {el.props.label}
                    </button>
                );
            }
        } else if (el.type === 'pfp') {
            const sizeMap = { sm: 'w-6 h-6', md: 'w-10 h-10', lg: 'w-16 h-16', xl: 'w-24 h-24' };
            content = (
                <div className={`${sizeMap[el.props.size as keyof typeof sizeMap]} rounded-full border-2 border-white/20 overflow-hidden shadow-2xl relative group/pfp`}>
                    <img src={`https://api.dicebear.com/7.x/bottts/svg?seed=${el.id}`} className="w-full h-full object-cover" alt="" />
                    {el.props.navigateToProfile && (
                        <div className="absolute inset-0 bg-blue-600/40 flex items-center justify-center opacity-0 group-hover/pfp:opacity-100 transition-opacity">
                            <ExternalLinkIcon className="w-1/2 h-1/2 text-white" />
                        </div>
                    )}
                </div>
            );
        } else if (el.type === 'tab') {
            content = (
                <div className="flex items-center gap-1 bg-black/40 p-1 rounded-xl border border-white/5">
                    {el.props.items.map((item: string, i: number) => (
                        <div key={i} className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest ${i === 0 ? 'bg-blue-600 text-white' : 'text-white/40'}`}>
                            {item}
                        </div>
                    ))}
                </div>
            );
        } else if (el.type === 'setting_bar') {
            content = (
                <div className="w-48 bg-white/5 border border-white/10 rounded-xl p-3 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-white/60 uppercase">{el.props.label}</span>
                    <div className="w-6 h-6 rounded-full border border-white/20" style={{ backgroundColor: el.props.color }} />
                </div>
            );
        }

        return (
            <div
                key={el.id}
                style={style}
                onMouseDown={(e) => handleDragStart(e, el.id)}
                onTouchStart={(e) => handleDragStart(e, el.id)}
                onContextMenu={(e) => {
                    e.preventDefault();
                    onRemoveElement(el.id);
                }}
                className="group"
            >
                {content}
                <button 
                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer shadow-lg z-[110]" 
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemoveElement(el.id);
                    }}
                >
                    <CloseIcon className="w-3 h-3 text-white" />
                </button>
            </div>
        );
    };

    return (
        <div 
            className="absolute inset-0 pointer-events-none"
            onMouseMove={handleDragMove}
            onTouchMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onTouchEnd={handleDragEnd}
        >
            <div className="w-full h-full relative pointer-events-auto overflow-hidden">
                {elements.map(renderElement)}
            </div>

            {/* Modal Preview Layer */}
            {activeModalId && (
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-6 pointer-events-auto">
                    <div className="w-full max-w-sm bg-[#1a1b1e] rounded-[2rem] border border-white/10 shadow-2xl p-6 animate-slide-in-up">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-black text-white uppercase tracking-widest italic">Preview Modal</h3>
                            <button onClick={() => setActiveModalId(null)} className="p-2 hover:bg-white/5 rounded-full text-white/40">
                                <CloseIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="h-24 rounded-2xl bg-white/5 border border-white/5 flex items-center justify-center">
                                <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.2em]">Modal Content Area</span>
                            </div>
                            <button 
                                onClick={() => setActiveModalId(null)}
                                className="w-full py-3 rounded-xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Configuration Overlay */}
            {configuringItem && (
                <div className="absolute inset-0 bg-black/80 backdrop-blur-md z-[400] flex items-center justify-center p-6 pointer-events-auto">
                    <div className="w-full max-w-xs bg-[#1a1b1e] rounded-[2rem] border border-white/10 shadow-2xl p-6 animate-slide-in-up">
                        <h3 className="text-sm font-black text-white uppercase tracking-widest mb-6 italic text-center">
                            Configure {configuringItem.type.replace('_', ' ')}
                        </h3>
                        
                        <div className="space-y-4 mb-8">
                            {configuringItem.type === 'button' && (
                                <>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Button Label</label>
                                        <input 
                                            type="text" 
                                            value={configLabel}
                                            onChange={(e) => setConfigLabel(e.target.value)}
                                            placeholder="Enter label (e.g. Follow)"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder:text-white/20 focus:border-blue-500/50 outline-none transition-all"
                                            autoFocus
                                        />
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Map Action</label>
                                        <select 
                                            value={configAction}
                                            onChange={(e) => setConfigAction(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="none">No Action</option>
                                            <option value="open_modal">Open Modal</option>
                                            <option value="copy">Copy Content</option>
                                            <option value="upload">Upload Attachment</option>
                                            <option value="record">Record Audio</option>
                                            <option value="message">Start Message</option>
                                            <option value="yipyap">Trigger YipYap</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {configuringItem.type === 'pfp' && (
                                <>
                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Size</label>
                                        <select 
                                            value={configLabel}
                                            onChange={(e) => setConfigLabel(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="sm">Small (sm)</option>
                                            <option value="md">Medium (md)</option>
                                            <option value="lg">Large (lg)</option>
                                            <option value="xl">Extra Large (xl)</option>
                                        </select>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Navigation</label>
                                        <select 
                                            value={configAction}
                                            onChange={(e) => setConfigAction(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white outline-none appearance-none cursor-pointer"
                                        >
                                            <option value="false">Static Only</option>
                                            <option value="true">Enable Profile Navigation</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {configuringItem.type === 'tab' && (
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Tab Items (comma separated)</label>
                                    <input 
                                        type="text" 
                                        value={configLabel}
                                        onChange={(e) => setConfigLabel(e.target.value)}
                                        placeholder="Feed, Likes, Tags"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder:text-white/20 focus:border-blue-500/50 outline-none transition-all"
                                        autoFocus
                                    />
                                </div>
                            )}

                            {configuringItem.type === 'setting_bar' && (
                                <div className="space-y-1.5">
                                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest px-1">Setting Label</label>
                                    <input 
                                        type="text" 
                                        value={configLabel}
                                        onChange={(e) => setConfigLabel(e.target.value)}
                                        placeholder="Text Color"
                                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs text-white placeholder:text-white/20 focus:border-blue-500/50 outline-none transition-all"
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <button 
                                onClick={() => setConfiguringItem(null)}
                                className="py-3 rounded-xl bg-white/5 text-white/40 text-[10px] font-black uppercase tracking-widest hover:text-white transition-all"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddWithConfig}
                                className="py-3 rounded-xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-600/20 active:scale-95 transition-all"
                            >
                                Add to Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating UI Elements */}
            <div className="absolute bottom-6 left-6 pointer-events-auto z-[200]">
                <button
                    onClick={() => setShowMenu(!showMenu)}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-2xl ${showMenu ? 'bg-red-600 rotate-45' : 'bg-blue-600 hover:scale-110'}`}
                >
                    <PlusIcon className="w-6 h-6 text-white" />
                </button>

                {showMenu && (
                    <div className="absolute bottom-16 left-0 w-64 max-h-[400px] overflow-y-auto bg-[#1a1b1e] border border-white/10 rounded-2xl shadow-2xl p-4 custom-scrollbar animate-slide-in-up">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-xs font-black text-white uppercase tracking-widest">UI Components</h4>
                            <span className="text-[8px] text-white/40 uppercase">Right-click to remove</span>
                        </div>
                        
                        {MODULAR_COMPONENTS.map((cat, i) => (
                            <div key={i} className="mb-4">
                                <h5 className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-2 px-1">{cat.category}</h5>
                                <div className="grid grid-cols-1 gap-1">
                                    {cat.items.map((item, j) => (
                                        <button
                                            key={j}
                                            onClick={() => {
                                                setConfiguringItem(item);
                                                // Initialize config based on type
                                                const props = item.props as any;
                                                if (item.type === 'pfp') {
                                                    setConfigLabel(props.size);
                                                    setConfigAction(String(props.navigateToProfile));
                                                } else if (item.type === 'tab') {
                                                    setConfigLabel(props.items.join(', '));
                                                } else {
                                                    setConfigLabel(props.label || '');
                                                    setConfigAction(props.action || 'none');
                                                }
                                            }}
                                            className="w-full text-left px-3 py-2 rounded-xl hover:bg-white/5 text-[10px] text-white/60 hover:text-white transition-all flex items-center justify-between group"
                                        >
                                            {item.label}
                                            <SparklesIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 text-blue-400" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
